/**
 * LeadsView — Phase B-Lite Owner CRM lead inbox.
 *
 * Lists captured leads from the public chat widget. Filter by status + intent.
 * Click a row to open a drawer showing the full chat transcript, status edit,
 * and notes for follow-up.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Inbox,
  Loader2,
  Mail,
  MessageSquare,
  Phone,
  RefreshCw,
  Search,
  Sparkles,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { crmApi } from "@/lib/ownerClient";

const STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "qualified", label: "Qualified" },
  { value: "converted", label: "Converted" },
  { value: "lost", label: "Lost" },
];

const INTENT_OPTIONS = [
  { value: "all", label: "All intents" },
  { value: "general", label: "General" },
  { value: "new_project", label: "New project" },
  { value: "maintenance_plan", label: "Maintenance plan" },
  { value: "issue", label: "Issue / warranty" },
];

const STATUS_STYLES = {
  new: "border-amber-500/40 text-amber-300 bg-amber-500/10",
  contacted: "border-blue-500/40 text-blue-300 bg-blue-500/10",
  qualified: "border-violet-500/40 text-violet-300 bg-violet-500/10",
  converted: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
  lost: "border-zinc-700 text-zinc-400 bg-zinc-500/10",
};

const INTENT_LABEL = {
  general: "General",
  new_project: "New project",
  maintenance_plan: "Maintenance plan",
  issue: "Issue",
};

const AGENT_NAME = {
  "pat-harper": "Pat",
  "sam-torres": "Sam",
  "jordan-hale": "Jordan",
};

function StatusBadge({ status }) {
  const cls = STATUS_STYLES[status] || STATUS_STYLES.new;
  return (
    <Badge
      variant="outline"
      className={`text-[10px] uppercase tracking-[0.14em] ${cls}`}
      data-testid={`lead-status-${status || "new"}`}
    >
      {status || "new"}
    </Badge>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (_err) {
    return iso;
  }
}

function LeadDetailDrawer({ open, leadId, onClose, onUpdated }) {
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusDraft, setStatusDraft] = useState("");
  const [notesDraft, setNotesDraft] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      if (!open) {
        if (active) setBundle(null);
        return;
      }
      if (!leadId) return;
      setLoading(true);
      try {
        const data = await crmApi.getLead(leadId);
        if (!active) return;
        setBundle(data);
        setStatusDraft(data?.lead?.status || "new");
        setNotesDraft(data?.lead?.notes || "");
      } catch (err) {
        if (active) toast.error(err.message || "Failed to load lead");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [open, leadId]);

  async function handleSave() {
    if (!leadId) return;
    setSaving(true);
    try {
      const updated = await crmApi.updateLead(leadId, {
        status: statusDraft,
        notes: notesDraft,
      });
      toast.success("Lead updated");
      setBundle((prev) => (prev ? { ...prev, lead: updated } : prev));
      onUpdated?.();
    } catch (err) {
      toast.error(err.message || "Update failed");
    } finally {
      setSaving(false);
    }
  }

  const lead = bundle?.lead || null;
  const session = bundle?.session || null;
  const messages = session?.messages || [];

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose?.()}>
      <SheetContent
        side="right"
        className="dark w-full sm:max-w-[520px] bg-black text-white border-l border-zinc-800 overflow-y-auto"
        data-testid="lead-detail-drawer"
      >
        <SheetHeader>
          <SheetTitle className="text-white">
            {lead?.name || "Lead"}
          </SheetTitle>
          <SheetDescription className="text-zinc-400">
            {lead?.email || ""}
            {lead?.phone ? ` · ${lead.phone}` : ""}
          </SheetDescription>
        </SheetHeader>

        {loading && (
          <div className="flex items-center justify-center py-10 text-zinc-500">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
          </div>
        )}

        {lead && !loading && (
          <div className="mt-4 space-y-5">
            {/* Quick stats */}
            <div className="grid grid-cols-2 gap-3 text-[12px]">
              <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3">
                <div className="uppercase tracking-[0.16em] text-[10px] text-zinc-500 mb-1">
                  Intent
                </div>
                <div className="text-white">{INTENT_LABEL[lead.intent] || lead.intent || "—"}</div>
              </div>
              <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3">
                <div className="uppercase tracking-[0.16em] text-[10px] text-zinc-500 mb-1">
                  Source page
                </div>
                <div className="text-white break-all">{lead.source_page || "—"}</div>
              </div>
              <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3">
                <div className="uppercase tracking-[0.16em] text-[10px] text-zinc-500 mb-1">
                  Captured
                </div>
                <div className="text-white">{formatDate(lead.created_at)}</div>
              </div>
              <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3">
                <div className="uppercase tracking-[0.16em] text-[10px] text-zinc-500 mb-1">
                  Source
                </div>
                <div className="text-white">{lead.source || "public_chat"}</div>
              </div>
            </div>

            {/* Status + notes form */}
            <div className="space-y-3 rounded-md border border-zinc-800 bg-zinc-950 p-3">
              <div>
                <Label className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-1.5 block">
                  Status
                </Label>
                <Select value={statusDraft} onValueChange={setStatusDraft}>
                  <SelectTrigger
                    className="h-9 bg-black border-zinc-800 text-white"
                    data-testid="lead-status-select"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.filter((o) => o.value !== "all").map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-1.5 block">
                  Notes
                </Label>
                <Textarea
                  value={notesDraft}
                  onChange={(e) => setNotesDraft(e.target.value)}
                  placeholder="Add internal notes for follow-up…"
                  data-testid="lead-notes"
                  className="min-h-[80px] bg-black border-zinc-800 text-white placeholder:text-zinc-600"
                />
              </div>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-amber-500 hover:bg-amber-400 text-black font-semibold"
                data-testid="lead-save"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
              </Button>
            </div>

            {/* Transcript */}
            <div>
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-2">
                <MessageSquare className="h-3 w-3" />
                Transcript
                <span className="lowercase tracking-normal text-zinc-600">
                  ({messages.length} messages)
                </span>
              </div>
              <div className="space-y-2 rounded-md border border-zinc-800 bg-zinc-950 p-3 max-h-[320px] overflow-y-auto" data-testid="lead-transcript">
                {messages.length === 0 && (
                  <div className="text-zinc-500 text-[12px]">No transcript captured.</div>
                )}
                {messages.map((m, idx) => {
                  const isUser = m.role === "user";
                  return (
                    <div
                      key={`${idx}-${m.role}`}
                      className={`flex flex-col ${isUser ? "items-end" : "items-start"}`}
                    >
                      <div className="text-[9px] uppercase tracking-[0.14em] text-zinc-600 mb-0.5">
                        {isUser ? "Visitor" : AGENT_NAME[m.agent_slug] || "Agent"}
                      </div>
                      <div
                        className={`text-[12.5px] leading-relaxed whitespace-pre-wrap break-words rounded-lg px-3 py-2 max-w-[88%] ${
                          isUser
                            ? "bg-amber-500/90 text-black"
                            : "bg-zinc-900 text-white border border-zinc-800"
                        }`}
                      >
                        {m.content}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default function LeadsView() {
  const [leads, setLeads] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [intentFilter, setIntentFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [openLeadId, setOpenLeadId] = useState(null);

  const load = useCallback(
    async (isRefresh = false) => {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      setError("");
      try {
        const params = {};
        if (statusFilter !== "all") params.status = statusFilter;
        if (intentFilter !== "all") params.intent = intentFilter;
        if (search.trim()) params.search = search.trim();
        const [list, sum] = await Promise.all([
          crmApi.listLeads(params),
          crmApi.leadsSummary(),
        ]);
        setLeads(list.leads || []);
        setSummary(sum);
      } catch (err) {
        setError(err.message || "Failed to load leads");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [statusFilter, intentFilter, search],
  );

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const params = {};
        if (statusFilter !== "all") params.status = statusFilter;
        if (intentFilter !== "all") params.intent = intentFilter;
        if (search.trim()) params.search = search.trim();
        const [list, sum] = await Promise.all([
          crmApi.listLeads(params),
          crmApi.leadsSummary(),
        ]);
        if (!active) return;
        setLeads(list.leads || []);
        setSummary(sum);
      } catch (err) {
        if (active) setError(err.message || "Failed to load leads");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
    // search is applied only when user clicks Search (handleSearchSubmit calls load(true))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, intentFilter]);

  function handleSearchSubmit(e) {
    e.preventDefault();
    load();
  }

  const kpiTiles = useMemo(() => {
    if (!summary) return [];
    return [
      { label: "Total leads", value: summary.total },
      { label: "New", value: summary.by_status?.new ?? 0 },
      { label: "Contacted", value: summary.by_status?.contacted ?? 0 },
      { label: "Qualified", value: summary.by_status?.qualified ?? 0 },
      { label: "Converted", value: summary.by_status?.converted ?? 0 },
    ];
  }, [summary]);

  return (
    <div className="space-y-6" data-testid="owner-leads-view">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold text-white">
            <Inbox className="h-5 w-5 text-amber-400" />
            Leads
          </h1>
          <p className="text-sm text-zinc-400">
            Captured by the public chat widget. Auto-routed to Pat → Sam / Jordan
            based on intent. Internal SendGrid alert + welcome_lead nurture fire on capture.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => load(true)}
          disabled={refreshing}
          className="border-zinc-800 text-white hover:bg-zinc-900"
          data-testid="leads-refresh"
        >
          {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          <span className="ml-2">Refresh</span>
        </Button>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {kpiTiles.map((tile) => (
          <Card key={tile.label} className="bg-zinc-950 border-zinc-800">
            <CardContent className="p-3">
              <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">
                {tile.label}
              </div>
              <div className="text-2xl font-semibold text-white">{tile.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:gap-3">
        <form onSubmit={handleSearchSubmit} className="flex-1 flex items-end gap-2">
          <div className="flex-1">
            <Label className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-1.5 block">
              Search
            </Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="name, email, phone…"
                className="h-9 pl-8 bg-zinc-950 border-zinc-800 text-white placeholder:text-zinc-600"
                data-testid="leads-search-input"
              />
            </div>
          </div>
          <Button type="submit" className="h-9 bg-amber-500 hover:bg-amber-400 text-black">
            Search
          </Button>
        </form>
        <div>
          <Label className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-1.5 block">
            Status
          </Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 w-[160px] bg-zinc-950 border-zinc-800 text-white" data-testid="leads-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-1.5 block">
            Intent
          </Label>
          <Select value={intentFilter} onValueChange={setIntentFilter}>
            <SelectTrigger className="h-9 w-[180px] bg-zinc-950 border-zinc-800 text-white" data-testid="leads-intent-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {INTENT_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table / list */}
      <div className="rounded-md border border-zinc-800 bg-zinc-950 overflow-hidden">
        <div className="grid grid-cols-12 text-[10px] uppercase tracking-[0.16em] text-zinc-500 px-4 py-2 border-b border-zinc-800">
          <div className="col-span-3">Lead</div>
          <div className="col-span-3">Contact</div>
          <div className="col-span-2">Intent</div>
          <div className="col-span-2">Source</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-1 text-right">When</div>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-12 text-zinc-500">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading leads…
          </div>
        )}
        {!loading && error && (
          <div className="px-4 py-6 text-red-300 text-[13px]">{error}</div>
        )}
        {!loading && !error && leads.length === 0 && (
          <div className="px-4 py-12 text-center" data-testid="leads-empty">
            <Sparkles className="h-6 w-6 text-amber-400 mx-auto mb-2" />
            <div className="text-white text-sm font-medium">No leads yet</div>
            <div className="text-zinc-500 text-[12px] mt-1">
              The public chat widget will land new leads here automatically.
            </div>
          </div>
        )}

        {!loading && !error && leads.map((lead) => (
          <button
            type="button"
            key={lead.id}
            onClick={() => setOpenLeadId(lead.id)}
            data-testid={`lead-row-${lead.id}`}
            className="w-full grid grid-cols-12 items-center px-4 py-3 border-b border-zinc-900 text-left hover:bg-zinc-900/60 transition-colors"
          >
            <div className="col-span-3 min-w-0">
              <div className="text-white font-medium truncate">{lead.name || "—"}</div>
              <div className="text-[11px] text-zinc-500 truncate">
                {lead.transcript_summary?.slice(0, 60) || ""}
              </div>
            </div>
            <div className="col-span-3 text-[12.5px] text-zinc-300 min-w-0">
              <div className="flex items-center gap-1.5 truncate">
                <Mail className="h-3 w-3 text-zinc-500 shrink-0" />
                <span className="truncate">{lead.email || "—"}</span>
              </div>
              {lead.phone ? (
                <div className="flex items-center gap-1.5 text-zinc-500 text-[11px]">
                  <Phone className="h-3 w-3 shrink-0" />
                  {lead.phone}
                </div>
              ) : null}
            </div>
            <div className="col-span-2">
              <Badge variant="outline" className="border-zinc-700 text-zinc-300 text-[10px]">
                {INTENT_LABEL[lead.intent] || lead.intent || "general"}
              </Badge>
            </div>
            <div className="col-span-2 text-[11.5px] text-zinc-400 truncate">
              {lead.source_page || "—"}
            </div>
            <div className="col-span-1">
              <StatusBadge status={lead.status} />
            </div>
            <div className="col-span-1 text-right text-[11px] text-zinc-500">
              {formatDate(lead.created_at)}
            </div>
          </button>
        ))}
      </div>

      <LeadDetailDrawer
        open={Boolean(openLeadId)}
        leadId={openLeadId}
        onClose={() => setOpenLeadId(null)}
        onUpdated={() => load(true)}
      />
    </div>
  );
}
